// src/components/JobDetails.jsx
import React, { useState } from 'react';
import './JobDetails.css';

const JobDetails = ({ job }) => {
  const [activeTab, setActiveTab] = useState('details');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Create date object from the UTC time
    const date = new Date(dateString);
    // This will convert to local time with a clear format
    return date.toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const duration = (endDate - startDate) / 1000; // in seconds
    
    if (duration < 60) return `${Math.round(duration)} seconds`;
    if (duration < 3600) return `${Math.round(duration / 60)} minutes`;
    return `${Math.round(duration / 3600)} hours ${Math.round((duration % 3600) / 60)} minutes`;
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'success': return 'status-success';
      case 'in_progress': return 'status-in-progress';
      case 'failed': return 'status-failed';
      case 'skipped': return 'status-skipped';
      case 'partial_success': return 'status-warning';
      default: return '';
    }
  };

  // Extract hardware IDs from JSON string
  const hwIds = typeof job.hw_ids === 'string' 
    ? JSON.parse(job.hw_ids) 
    : job.hw_ids || [];

  return (
    <div className="job-details">
      <div className="job-header">
        <h2>Job Details</h2>
        {/* <h2>{job.job_id}</h2> */}
        <div className={`job-status ${getStatusClass(job.status)}`}>
          {job.status.replace('_', ' ')}
        </div>
      </div>
      
      <div className="tabs">
        <button 
          className={activeTab === 'details' ? 'active' : ''} 
          onClick={() => setActiveTab('details')}
        >
          Overview
        </button>
        <button 
          className={activeTab === 'tables' ? 'active' : ''} 
          onClick={() => setActiveTab('tables')}
        >
          Tables <span>
           {job.table_deliveries?.length || 0}
          </span>
        </button>
        <button 
          className={activeTab === 'errors' ? 'active' : ''} 
          onClick={() => setActiveTab('errors')}
        >
          Errors <span>
            {job.failures?.length || 0}
          </span>
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'details' && (
          <div className="details-tab">
            <div className="detail-tab-grid">
              <div className="detail-row">
                <span className="label">Business:</span>
                <span className="value">{job.business_name}</span>
              </div>
              <div className="detail-row">
                <span className="label">User:</span>
                <span className="value">{job.user_email}</span>
              </div>
              <div className="detail-row">
                <span className="label">Scheduled at:</span>
                <span className="value">{formatDate(job.scheduled_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Started at:</span>
                <span className="value">{formatDate(job.started_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Completed at:</span>
                <span className="value">{formatDate(job.completed_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Duration:</span>
                <span className="value">{formatDuration(job.started_at, job.completed_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Progress:</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${job.total_tables_count > 0 
                        ? (job.successful_tables_count / job.total_tables_count) * 100 
                        : 0}%` 
                    }}
                  ></div>
                  <span className="progress-text">
                    {job.successful_tables_count} / {job.total_tables_count} tables
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className="hw-ids-header">{hwIds && hwIds.length} Site(s):</div>
              <div className='hw-ids'>
                {hwIds.length > 0 ? (
                  hwIds.map((item, index) => (
                    <span key={index} className="hw-id">
                      <span />
                      {item.site_name}
                    </span>
                  ))
                ) : (
                  <span>None</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'tables' && (
          <div className="tables-tab">
            <div className="table-header">
              <span className="table-name-col">Table Name</span>
              <span className="records-col">Records</span>
              <span className="attempt-col">Attempt</span>
              <span className="status-col">Status</span>
            </div>
            <div className="tables-container">
              {job.table_deliveries && job.table_deliveries.length > 0 ? (
                job.table_deliveries.map(table => {
                  const config = typeof table.table_config === 'string' 
                    ? JSON.parse(table.table_config) 
                    : table.table_config;
                  
                  return (
                    <div key={table.transfer_id} className="table-row">
                      <span className="table-name-col">{config?.targetName || config?.name || 'Unknown'}</span>
                      <span className="records-col">{table.records_transferred}</span>
                      <span className="attempt-col">{table.attempt_number}</span>
                      <span className={`status-col job-status ${getStatusClass(table.status)} col`}>
                        {table.status}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="no-tables">No table transfers found</div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'errors' && (
          <div className="errors-tab">
            {job.failures && job.failures.length > 0 ? (
              job.failures.map(error => (
                <div key={error.error_id} className="error-card">
                  <div className="error-header">
                    <span className="error-name">{error.error_name || 'Unknown Error'}</span>
                    <span className="error-code">{error.error_code || 'No Code'}</span>
                    <span className="error-time">{formatDate(error.created_at)}</span>
                  </div>
                  <div className="error-message">
                    {error.error_message || 'No error message provided'}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-errors">No errors reported</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetails;